import { type FC, type MouseEvent, useContext, useMemo } from "react";

import { PopoverPlacementTypes, Tooltip } from "@snapchat/snap-design-system";

import { type MyLensesLens } from "../../../gql-generated/types";
import { UserInfoContext } from "../../../shared/api/contexts";
import { useLensDestinations } from "../../hooks/useLensDestinations";

import { zIndex } from "./LensDestinationsTooltip.styles";
import { LensDestinationsTooltipContent } from "./LensDestinationsTooltipContent";
import { LensDestinationTooltipTitle } from "./messages.generated";

export interface LensDestinationsTooltipProps {
    lens: MyLensesLens;
    numDestinations?: number;
    onDestinationHeaderClick?: (event: MouseEvent<HTMLElement>) => void;
    isDisabledInDiscoverEnabled?: boolean;
}
export const LensDestinationsTooltip: FC<LensDestinationsTooltipProps> = ({
    lens,
    numDestinations,
    onDestinationHeaderClick,
    isDisabledInDiscoverEnabled = false,
}) => {
    const { id: lensId, distributionsList: lensDistributionsList, lensAccountId, distributionRestrictionsList } = lens;

    const lensFolderId = lensAccountId?.lensSpaceId ?? "";
    const { loading, destinations } = useLensDestinations(lensId, lensFolderId, lensDistributionsList);

    const userInfo = useContext(UserInfoContext);

    const content = useMemo(
        () => (
            <LensDestinationsTooltipContent
                loading={loading || !!userInfo?.loading}
                destinations={destinations}
                numDestinations={numDestinations}
                userId={userInfo?.data?.getUserProfile.userId ?? ""}
                distributionRestrictionsList={isDisabledInDiscoverEnabled ? distributionRestrictionsList : []}
                onLensDestinationHeaderClick={onDestinationHeaderClick}
            />
        ),
        [
            numDestinations,
            loading,
            destinations,
            userInfo,
            distributionRestrictionsList,
            onDestinationHeaderClick,
            isDisabledInDiscoverEnabled,
        ],
    );

    return (
        <Tooltip
            className={zIndex}
            title={<LensDestinationTooltipTitle />}
            arrowPointAtCenter={true}
            visible={true}
            placement={PopoverPlacementTypes.BOTTOM}
            content={content}
        />
    );
};
